import React, { useState, useEffect, useCallback } from "react";
import { Spinner, Alert } from "react-bootstrap";
import { Link } from "react-router-dom";
import axios from "axios";
import { FiCalendar, FiClock, FiActivity, FiExternalLink } from "react-icons/fi";
import { FaSyringe, FaShieldVirus, FaStethoscope } from "react-icons/fa";

const formatDate = (value) => {
    if (!value) return "-";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;

    return new Intl.DateTimeFormat("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    }).format(date);
};

const formatAmount = (value) => {
    if (value === null || value === undefined) return "-";
    return parseFloat(value).toLocaleString(undefined, { minimumFractionDigits: 2 });
};

const statusTone = (status) => {
    switch ((status || "").toLowerCase()) {
        case "completed":
            return "is-green";
        case "cancelled":
        case "missed":
            return "is-red";
        case "scheduled":
            return "is-blue";
        default:
            return "is-gray";
    }
};

const paymentTone = (status) => {
    switch ((status || "").toLowerCase()) {
        case "paid":
            return "is-green";
        case "partial":
            return "is-amber";
        case "unpaid":
            return "is-red";
        default:
            return "is-gray";
    }
};

/**
 * Everything on record for one pet. The customer profile opens this under a pet
 * card so a visit, the jabs given on it and the procedures booked for it are all
 * read in one place.
 */
const PetRecordsPanel = ({ petId, petName }) => {
    const [records, setRecords] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeView, setActiveView] = useState("appointments");

    const fetchRecords = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/customers-pet-records/${petId}`,
                { headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` } }
            );

            if (response.data.success) {
                setRecords(response.data);
                setError(null);
            } else {
                setError(response.data.message || "Failed to load pet records");
            }
        } catch (err) {
            console.error("Error fetching pet records:", err);
            setError(err.response?.data?.message || "Failed to load pet records");
        } finally {
            setLoading(false);
        }
    }, [petId]);

    useEffect(() => {
        fetchRecords();
    }, [fetchRecords]);

    if (loading) {
        return (
            <div className="cp-empty">
                <Spinner animation="border" variant="primary" size="sm" />
                <p>Loading records for {petName}...</p>
            </div>
        );
    }

    if (error) {
        return (
            <Alert variant="danger" className="mb-0">
                {error}
            </Alert>
        );
    }

    const appointments = records?.appointments || [];
    const vaccines = records?.vaccines || [];
    const dewormings = records?.dewormings || [];
    const procedures = records?.procedures || [];
    const summary = records?.summary || {};

    const views = [
        { key: "appointments", label: "Appointments", icon: <FiCalendar size={13} />, count: appointments.length },
        { key: "vaccines", label: "Vaccines", icon: <FaSyringe size={12} />, count: vaccines.length },
        { key: "dewormings", label: "Deworming", icon: <FaShieldVirus size={12} />, count: dewormings.length },
    ];

    /**
     * A jab or a dose is a visit of its own: what was given, by whom, when the
     * next one falls due and what it cost. A card holds all of that at once,
     * which a row of a table cannot.
     */
    const renderMedicalCards = (rows, emptyLabel, emptyIcon, fallbackTitle) => {
        if (rows.length === 0) {
            return (
                <div className="cp-empty">
                    {emptyIcon}
                    <p>{emptyLabel}</p>
                </div>
            );
        }

        return (
            <div className="cp-records">
                {rows.map((row) => (
                    <div
                        key={`${row.type}-${row.group_id}`}
                        className={`cp-record ${row.is_completed ? "is-done" : row.is_overdue ? "is-due" : ""
                            }`}
                    >
                        <div className="cp-record-head">
                            <div>
                                <h6 className="cp-record-title">
                                    {row.items_label || fallbackTitle}
                                </h6>
                                <span className="cp-record-sub">
                                    {formatDate(row.date)}
                                    {row.doctor_name ? ` · ${row.doctor_name}` : ""}
                                    {row.appointment_id ? " · " : ""}
                                    {row.appointment_id && (
                                        <Link to={`/appointments/${row.appointment_id}`}>
                                            {row.appointment_number || `APT-${row.appointment_id}`}
                                        </Link>
                                    )}
                                </span>
                            </div>
                            <div className="cp-item-tags">
                                <span className={`cp-tag ${row.is_completed ? "is-green" : "is-amber"}`}>
                                    {row.is_completed ? "Completed" : "Pending"}
                                </span>
                                {row.is_overdue && (
                                    <span className="cp-tag is-red">
                                        <FiClock size={10} /> Overdue
                                    </span>
                                )}
                            </div>
                        </div>

                        {row.items?.length > 0 && (
                            <div className="cp-item-tags mb-2">
                                {row.items.map((item, index) => (
                                    <span key={index} className="cp-tag is-violet">
                                        {item.name}
                                        {item.quantity > 1 ? ` x ${item.quantity}` : ""}
                                    </span>
                                ))}
                            </div>
                        )}

                        <div className="cp-record-grid">
                            <div>
                                <span>Given</span>
                                <strong>{formatDate(row.date)}</strong>
                            </div>

                            <div>
                                <span>Amount</span>
                                <strong>
                                    {row.total_price === null || row.total_price === undefined
                                        ? "-"
                                        : formatAmount(row.total_price)}
                                </strong>
                            </div>
                            {row.temperature ? (
                                <div>
                                    <span>Temperature</span>
                                    <strong>{row.temperature}</strong>
                                </div>
                            ) : null}
                        </div>

                        {row.recommendation && (
                            <p className="cp-record-note">{row.recommendation}</p>
                        )}
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className="cp-pet-records">
            <div className="cp-chips mb-3">
                {views.map((view) => (
                    <button
                        key={view.key}
                        type="button"
                        className={`cp-chip ${activeView === view.key ? "active" : ""}`}
                        onClick={() => setActiveView(view.key)}
                    >
                        {view.icon}
                        {view.label}
                        <span className="cp-chip-count">{view.count}</span>
                    </button>
                ))}
            </div>

            {activeView === "appointments" && (
                appointments.length === 0 ? (
                    <div className="cp-empty">
                        <FiCalendar size={30} />
                        <p>No appointments recorded for {petName}.</p>
                    </div>
                ) : (
                    <div className="cp-records">
                        {appointments.map((appointment) => (
                            <div
                                key={appointment.id}
                                className={`cp-record ${appointment.due_amount > 0 ? "is-due" : ""}`}
                            >
                                <div className="cp-record-head">
                                    <div>
                                        <h6 className="cp-record-title">
                                            <Link to={`/appointments/${appointment.id}`}>
                                                {appointment.appointment_number}{" "}
                                                <FiExternalLink size={11} />
                                            </Link>
                                        </h6>
                                        <span className="cp-record-sub">
                                            {formatDate(appointment.appointment_date)}
                                            {appointment.appointment_time_slot
                                                ? ` · ${appointment.appointment_time_slot}`
                                                : ""}
                                            {appointment.doctor_name ? ` · ${appointment.doctor_name}` : ""}
                                        </span>
                                    </div>
                                    <div className="cp-item-tags">
                                        <span className={`cp-tag ${statusTone(appointment.status)}`}>
                                            {appointment.status || "unknown"}
                                        </span>
                                        <span className={`cp-tag ${paymentTone(appointment.payment_status)}`}>
                                            {appointment.payment_status || "-"}
                                        </span>
                                    </div>
                                </div>

                                {/* The procedure is the reason for the visit, so it is the
                                    one thing called out rather than listed with the rest. */}
                                {appointment.procedures?.length > 0 && (
                                    <div className="cp-item-tags mb-2">
                                        {appointment.procedures.map((procedure) => (
                                            <span
                                                key={procedure.id}
                                                className="cp-tag is-violet cp-procedure-tag"
                                            >
                                                <FiActivity size={10} /> {procedure.name}
                                            </span>
                                        ))}
                                    </div>
                                )}

                                {appointment.services?.length > 0 && (
                                    <div className="cp-item-tags mb-2">
                                        {appointment.services.map((service, index) => (
                                            <span key={index} className="cp-tag is-gray">
                                                {service.name}
                                                {service.price ? ` · ${formatAmount(service.price)}` : ""}
                                            </span>
                                        ))}
                                    </div>
                                )}

                                <div className="cp-record-grid">
                                    <div>
                                        <span>Billed</span>
                                        <strong>{formatAmount(appointment.total_amount)}</strong>
                                    </div>
                                    <div>
                                        <span>Paid</span>
                                        <strong>{formatAmount(appointment.paid_amount)}</strong>
                                    </div>
                                    <div>
                                        <span>Due</span>
                                        <strong>{formatAmount(appointment.due_amount)}</strong>
                                    </div>
                                    <div>
                                        <span>Entered by</span>
                                        <strong>{appointment.entered_by || "-"}</strong>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )
            )}

            {activeView === "procedures" && (
                procedures.length === 0 ? (
                    <div className="cp-empty">
                        <FaStethoscope size={30} />
                        <p>No procedures recorded for {petName}.</p>
                    </div>
                ) : (
                    <div className="cp-table-wrap">
                        <table className="cp-table">
                            <thead>
                                <tr>
                                    <th>Procedure</th>
                                    <th>Appointment</th>
                                    <th>Date</th>
                                    <th>Status</th>
                                    <th className="text-end">Price</th>
                                </tr>
                            </thead>
                            <tbody>
                                {procedures.map((procedure) => (
                                    <tr key={procedure.id}>
                                        <td>
                                            <span className="cp-tag is-violet cp-procedure-tag">
                                                <FiActivity size={10} /> {procedure.name}
                                            </span>
                                        </td>
                                        <td>
                                            <Link to={`/appointments/${procedure.appointment_id}`}>
                                                {procedure.appointment_number}
                                            </Link>
                                        </td>
                                        <td>{formatDate(procedure.appointment_date)}</td>
                                        <td>
                                            <span className={`cp-tag ${statusTone(procedure.appointment_status)}`}>
                                                {procedure.appointment_status || "unknown"}
                                            </span>
                                        </td>
                                        <td className="text-end">{formatAmount(procedure.price)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )
            )}

            {activeView === "vaccines" &&
                renderMedicalCards(
                    vaccines,
                    `No vaccines recorded for ${petName}.`,
                    <FaSyringe size={30} />,
                    "Vaccine"
                )}

            {activeView === "dewormings" &&
                renderMedicalCards(
                    dewormings,
                    `No deworming recorded for ${petName}.`,
                    <FaShieldVirus size={30} />,
                    "Deworming"
                )}

            <div className="cp-totals mt-3">
                <div className="cp-total">
                    <span>Visits</span>
                    <strong>{summary.appointments_total ?? 0}</strong>
                </div>
                <div className="cp-total">
                    <span>Billed</span>
                    <strong>{formatAmount(summary.total_billed ?? 0)}</strong>
                </div>
                <div className="cp-total">
                    <span>Paid</span>
                    <strong>{formatAmount(summary.total_paid ?? 0)}</strong>
                </div>
                <div className={`cp-total ${(summary.total_due ?? 0) > 0 ? "is-negative" : ""}`}>
                    <span>Due</span>
                    <strong>{formatAmount(summary.total_due ?? 0)}</strong>
                </div>
                {summary.next_due_date && (
                    <div className="cp-total">
                        <span>Next reminder</span>
                        <strong>{formatDate(summary.next_due_date)}</strong>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PetRecordsPanel;
